import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";

import { useStateMachineValue } from "@/core/arch/state_machine";
import { StaffListBloc } from "@/application/staff/staff_list_bloc";
import { StaffRepository } from "@/application/staff/staff_repository";
import {
  STAFF_PERMISSION_LABELS,
  type StaffRow,
} from "@/application/staff/types";

import {
  formatRelativeTime,
  type BusinessProfile,
  type UserProfile,
} from "../types";
import { OverlayRow } from "./OverlaySection";
import { parseAdditionalPersons, type AdditionalPerson } from "./person";
import { TapToPayEducationToggle } from "./TapToPayEducationToggle";

/**
 * The three populations that count as "people" on a merchant, each arriving
 * from a different place and each meaning something different:
 *
 * - `user` — an account with a login. The owner is the oldest one.
 * - `staff` — a terminal operator, with permissions but no admin login.
 * - `person` — someone on the Stripe KYC record who may have neither.
 */
type PersonKind = "user" | "staff" | "person";

interface PersonEntry {
  id: string;
  kind: PersonKind;
  name: string;
  title: string;
  detail: ReactNode;
}

const TITLE_STYLES = new Map<string, string>([
  ["Owner", "bg-blue-50 text-blue-700 ring-blue-200"],
  ["Staff", "bg-muted text-muted-foreground ring-border"],
]);

function TitlePill({ title }: { title: string }) {
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        TITLE_STYLES.get(title) ??
        "bg-violet-50 text-violet-700 ring-violet-200"
      }`}
    >
      {title}
    </span>
  );
}

function Row({ entry }: { entry: PersonEntry }) {
  const [open, setOpen] = useState(false);

  return (
    <li className="border-border border-b last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="hover:bg-muted -mx-2 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
      >
        <ChevronRight
          className={`text-subtle-foreground h-3 w-3 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span className="text-foreground min-w-0 flex-1 truncate text-sm">
          {entry.name}
        </span>
        <TitlePill title={entry.title} />
      </button>

      {open && <div className="pb-2 pl-5">{entry.detail}</div>}
    </li>
  );
}

function userEntry(
  user: UserProfile,
  isOwner: boolean,
  onRefresh: () => void,
): PersonEntry {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");

  return {
    id: `user:${user.userId}`,
    kind: "user",
    name: name || user.email || user.userId,
    title: isOwner ? "Owner" : "User",
    detail: (
      <>
        <OverlayRow label="Email">{user.email ?? null}</OverlayRow>
        <OverlayRow label="Last login">
          {user.lastLogin ? formatRelativeTime(user.lastLogin) : "Never"}
        </OverlayRow>
        <OverlayRow label="Tap to Pay education seen">
          <TapToPayEducationToggle user={user} onRefresh={onRefresh} />
        </OverlayRow>
        <OverlayRow label="User ID">
          <span className="font-mono text-xs break-all">{user.userId}</span>
        </OverlayRow>
      </>
    ),
  };
}

function staffEntry(staff: StaffRow): PersonEntry {
  // Read through a Map rather than by dynamic key, so a permission name
  // arriving from data can never index into the object's prototype.
  const held = new Map<string, unknown>(Object.entries(staff.permissions));
  const granted = Object.entries(STAFF_PERMISSION_LABELS)
    .filter(([key]) => held.get(key) === true)
    .map(([, label]) => label);

  return {
    id: `staff:${staff.id}`,
    kind: "staff",
    // Owner staff are the merchant themselves on the terminal, not a hire.
    name: staff.name,
    title: staff.isOwner ? "Owner" : "Staff",
    detail: (
      <>
        <OverlayRow label="Email">{staff.email}</OverlayRow>
        <OverlayRow label="Active">{staff.isActive ? "Yes" : "No"}</OverlayRow>
        <OverlayRow label="Permissions">
          {granted.length > 0 ? granted.join(", ") : null}
        </OverlayRow>
      </>
    ),
  };
}

function personEntry(person: AdditionalPerson, index: number): PersonEntry {
  const roles = [
    person.isOwner ? "Owner" : null,
    person.isDirector ? "Director" : null,
    person.isExecutive ? "Executive" : null,
  ].filter(Boolean);

  return {
    id: `person:${person.stripePersonId ?? person.localId ?? index}`,
    kind: "person",
    name: `${person.firstName} ${person.lastName}`.trim(),
    title: roles[0] ?? "Person",
    detail: (
      <>
        <OverlayRow label="Email">{person.email ?? null}</OverlayRow>
        <OverlayRow label="Roles">
          {roles.length > 0 ? roles.join(", ") : null}
        </OverlayRow>
        <OverlayRow label="Ownership">
          {person.ownershipPercentage != null
            ? `${person.ownershipPercentage}%`
            : null}
        </OverlayRow>
        <OverlayRow label="Stripe person">
          {person.stripePersonId ? (
            <span className="font-mono text-xs break-all">
              {person.stripePersonId}
            </span>
          ) : null}
        </OverlayRow>
      </>
    ),
  };
}

/**
 * Everyone attached to this merchant, in one list.
 *
 * Users, staff and Stripe persons are merged because the question being
 * asked is "who is on this account", not "which table is this row in". The
 * pill says which kind of person, and the expanded detail differs by kind
 * because what matters about a staff member (permissions) is not what
 * matters about a user (last login).
 */
export function PeopleList({
  business,
  users,
  onRefresh,
}: {
  business: BusinessProfile;
  users: UserProfile[] | null;
  onRefresh: () => void;
}) {
  const bloc = useMemo(
    () => new StaffListBloc(new StaffRepository(), business.id),
    [business.id],
  );
  const [staffState] = useStateMachineValue(bloc);

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  const entries: PersonEntry[] = [];

  if (users) {
    // Oldest profile is the owner, matching how the sheet resolves it above.
    const ordered = [...users].sort((a, b) =>
      (a.createdAt ?? "") < (b.createdAt ?? "") ? -1 : 1,
    );
    ordered.forEach((user, i) =>
      entries.push(userEntry(user, i === 0, onRefresh)),
    );
  }

  staffState.items.forEach((staff) => entries.push(staffEntry(staff)));

  parseAdditionalPersons(business.additionalPersons).forEach((person, i) =>
    entries.push(personEntry(person, i)),
  );

  return (
    <div>
      {!users && (
        <p className="mb-2 text-sm text-amber-700">
          Could not load users — this list is incomplete.
        </p>
      )}
      {staffState.status === "error" && (
        <p className="mb-2 text-sm text-amber-700">
          Could not load staff — this list is incomplete.
        </p>
      )}

      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nobody on this account yet.
        </p>
      ) : (
        <ul>
          {entries.map((entry) => (
            <Row key={entry.id} entry={entry} />
          ))}
        </ul>
      )}
    </div>
  );
}
